import { useRef, useState } from "react";
import { Gift, Ticket } from "lucide-react";
import { api, USER_ID } from "../api";
import { toast } from "sonner";
import { badgeToast, melaToast, MelaCoin } from "./BadgesSheet";

const hoursLeft = (iso) => Math.max(0, Math.round((new Date(iso).getTime() - Date.now()) / 3600000));

const ScratchCard = ({ card, refresh }) => {
  const canvasRef = useRef(null);
  const strokes = useRef(0);
  const revealing = useRef(false);
  const [prize, setPrize] = useState(card.prize);
  const [cleared, setCleared] = useState(card.status === "revealed");

  const reveal = async () => {
    if (revealing.current || prize) return null;
    revealing.current = true;
    try {
      const r = await api.post(`/arcade/scratch/${card.card_id}`, { user_id: USER_ID });
      setPrize(r.data);
      badgeToast(r.data.new_badges);
      refresh();
      return r.data;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not scratch this card");
      revealing.current = false;
      return null;
    }
  };

  const initFoil = (cv) => {
    if (!cv || cv.dataset.done) return;
    cv.dataset.done = "1";
    const ctx = cv.getContext("2d");
    const g = ctx.createLinearGradient(0, 0, cv.width, cv.height);
    g.addColorStop(0, "#8a6d1f"); g.addColorStop(0.5, "#D4AF37"); g.addColorStop(1, "#8a6d1f");
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, cv.width, cv.height);
    ctx.fillStyle = "#3d3110";
    ctx.font = "900 15px sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("SCRATCH ME", cv.width / 2, cv.height / 2 + 5);
  };

  const scratchAt = (e) => {
    const cv = canvasRef.current;
    if (!cv) return;
    const rect = cv.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * cv.width;
    const y = ((e.clientY - rect.top) / rect.height) * cv.height;
    const ctx = cv.getContext("2d");
    ctx.globalCompositeOperation = "destination-out";
    ctx.beginPath();
    ctx.arc(x, y, 20, 0, Math.PI * 2);
    ctx.fill();
    strokes.current += 1;
    if (strokes.current > 24) setCleared(true);
  };

  const onDown = (e) => { e.preventDefault(); reveal(); scratchAt(e); };
  const onMove = (e) => { if (e.buttons || e.pressure > 0) scratchAt(e); };

  return (
    <div data-testid={`scratch-card-${card.card_id}`} className="rounded-xl border border-[#D4AF37]/40 bg-[#1C160A] p-3">
      <div className="relative h-[110px] rounded-lg overflow-hidden bg-[#0E0C08] flex items-center justify-center">
        <div className="text-center px-3">
          {prize ? (
            <>
              <p className="font-display text-xl font-black text-[#D4AF37]">{prize.label}</p>
              {prize.code && (
                <p data-testid={`scratch-code-${card.card_id}`} className="mt-1 flex items-center justify-center gap-1.5 text-xs">
                  <Ticket size={13} className="text-[#D4AF37]" />
                  <span className="font-mono font-black text-[#D4AF37] tracking-widest">{prize.code}</span>
                  <span className="text-white/50">· show at counter</span>
                </p>
              )}
              {prize.points_result && <p className="text-[11px] text-white/60 mt-1">{prize.points_result.message}</p>}
              {prize.mela_result && <p className="text-[11px] text-[#F0C742] font-bold mt-1 flex items-center justify-center gap-1.5"><MelaCoin size={14} /> {prize.mela_result.message}</p>}
            </>
          ) : (
            <p className="text-white/30 text-2xl font-black">?</p>
          )}
        </div>
        {!cleared && (
          <canvas ref={(cv) => { canvasRef.current = cv; initFoil(cv); }} width={560} height={220}
            data-testid={`scratch-foil-${card.card_id}`}
            className="absolute inset-0 w-full h-full touch-none cursor-pointer"
            onPointerDown={onDown} onPointerMove={onMove} />
        )}
      </div>
      <div className="flex items-center justify-between mt-2">
        <p className="text-[10px] text-white/40">
          {card.status === "revealed" || prize ? "Revealed" : `Expires in ${hoursLeft(card.expires_at)}h`}
        </p>
        {!cleared && (
          <button data-testid={`scratch-reveal-${card.card_id}`}
            onClick={async () => { await reveal(); setCleared(true); }}
            className="text-[9px] font-black tracking-widest text-[#D4AF37] border border-[#D4AF37]/50 rounded-full px-3 py-1.5">
            REVEAL
          </button>
        )}
      </div>
    </div>
  );
};

export const GoldenScratch = ({ state, refresh }) => {
  const cards = state.scratch_cards || [];
  return (
    <div data-testid="game-golden-scratch" className="rounded-2xl bg-[#121212] border border-[#D4AF37]/30 p-5">
      <p className="text-[10px] tracking-[0.3em] text-[#D4AF37] font-bold">SUNEHRI PATTI · ਸੁਨਹਿਰੀ ਪੱਟੀ</p>
      <p className="text-xs text-white/50 mt-1">Every completed order drops a golden card in the tent. Scratch within 48 hours!</p>
      {cards.length === 0 ? (
        <div data-testid="scratch-empty" className="mt-4 rounded-xl border border-dashed border-[#D4AF37]/30 p-6 text-center">
          <Gift size={22} className="mx-auto text-[#D4AF37]/50" />
          <p className="text-sm text-white/50 mt-2">No cards yet — place an order and a golden card appears after it's served.</p>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 mt-4">
          {cards.map((c) => <ScratchCard key={c.card_id} card={c} refresh={refresh} />)}
        </div>
      )}
    </div>
  );
};
